/** Interface strings, in the two languages the workspace speaks.
 *
 *  The language comes from the account, not the browser: the same person sees
 *  the same words on every machine, and the server writes its e-mails in that
 *  language too. */

export type Locale = 'en' | 'pt';

const en = {
  signInTitle: 'Your workspace',
  signInLead: 'Put in your e-mail and we send you a link. There is no password.',
  email: 'E-mail',
  send: 'Send',
  linkSent: 'Check your e-mail. The link works once and lasts twenty minutes.',
  signingIn: 'Signing in',
  signIn: 'Sign in',
  signOut: 'Sign out',
  continueAs: (name: string) => `Continue as ${name}`,
  linkTrouble: 'Ask for another link',

  projects: 'Projects',
  tasks: 'Tasks',
  time: 'Time',
  people: 'People',
  workspace: 'Client',
  profile: 'Profile',
  settings: 'Settings',
  editProfile: 'Edit your details',
  backToOverview: 'Back to your overview',
  files: 'Files',
  about: 'About',
  history: 'History',

  noProjects: 'Nothing here yet.',
  nothingInStatus: (status: string) => `Nothing ${status.toLowerCase()}.`,
  showAll: 'Show every project',
  noTasks: 'No tasks.',
  noTime: 'No time logged.',
  noComments: 'No comments yet.',

  hoursUsed: 'used',
  hoursOf: 'of',
  hoursOver: 'over',
  openTasks: (n: number) => (n === 1 ? '1 open task' : `${n} open tasks`),
  taskCount: (n: number) => (n === 1 ? '1 task' : `${n} tasks`),
  projectCount: (n: number) => (n === 1 ? '1 project' : `${n} projects`),
  newTask: 'New task',
  createTask: 'Create task',
  title: 'Title',
  description: 'Description',
  priority: 'Priority',
  assignee: 'Assigned to',
  state: 'State',
  approve: 'Approve',
  approveHint: 'This is waiting for you.',
  comment: 'Comment',
  comments: 'Comments',
  writeComment: 'Write a comment',
  internalComment: 'Internal note: only the team sees it',
  internalBadge: 'Internal',
  post: 'Post',
  imageHint: 'Write here, and paste or drop images straight in.',
  uploadFailed: 'An image did not upload. Try again.',
  imageSize: 'Image size',
  formatting: 'Formatting',
  bold: 'Bold',
  italic: 'Italic',
  bulletList: 'Bulleted list',
  numberedList: 'Numbered list',
  link: 'Link',
  linkAddress: 'Link address (leave empty to remove the link)',
  image: 'Image',
  logTime: 'Log time',
  date: 'Date',
  started: 'Started',
  minutes: 'Minutes',
  what: 'What',
  project: 'Project',
  person: 'Person',
  hours: 'Hours',
  download: 'Download CSV',
  recentTime: 'Your last two weeks',
  myTasks: 'Your tasks',
  myTasksHint: 'Assigned to you, new or under way.',
  total: 'Total',
  seeEntries: 'See the entries',
  entries: (n: number) => (n === 1 ? '1 entry' : `${n} entries`),
  from: 'From',
  to: 'To',
  clear: 'Clear',
  remove: 'Remove',
  confirmRemove: 'Remove this entry?',
  confirmDeleteComment: 'Delete this comment? It cannot be brought back.',
  addPerson: 'Add someone',
  noPeople: 'Nobody yet.',
  confirmRemovePerson: 'Take this person off the project? They lose sight of it.',
  onADocument: 'On an open document',
  onADocumentHint: 'These hours are on a document nobody has finished with. Throw it away to change them.',
  noFilters: 'Everything, most recent first.',
  shortBy: (h: string) => `${h}h short`,
  nothingLogged: 'Nothing logged',
  export: 'Export',
  client: 'Client',
  users: 'People',
  newUser: 'Add someone',
  invite: 'Add',
  role: 'Role',
  team: 'Studio team',
  administrator: 'Administrator',
  active: 'Active',
  inactive: 'Deactivated',
  showInactive: 'Show deactivated',
  workspaces: 'Clients',
  edit: 'Edit',
  cancel: 'Cancel',
  newProject: 'New project',
  editProject: 'Edit project',
  status: 'Status',
  totalHours: 'Hours bought',
  startDate: 'Start',
  endDate: 'End',
  manager: 'Manager',
  lastSeen: 'Last seen',
  chooseEntries: 'Choose entries',
  hideEntries: 'Hide entries',
  leftOut: (n: number) => (n === 1 ? '1 entry left for next time' : `${n} entries left for next time`),
  never: 'Never',
  owner: 'Owner',
  published: 'Visible to the client',
  create: 'Create',
  none: '—',
  search: 'Search',
  menu: 'Menu',
  close: 'Close',
  clients: 'Clients',
  newClient: 'Add a client',
  clientName: 'Name',
  lastWorked: 'Last worked on',
  hoursByMonth: 'Hours, by month',
  members: 'Who sees this',
  noActivity: 'Nothing logged yet.',
  bought: 'bought',
  hoursLeft: 'left',
  editClient: 'Edit client',
  billing: 'Billing',
  toInvoice: 'Waiting to be invoiced',
  documents: 'Documents',
  documentKind: 'Kind',
  noDocuments: 'No documents here yet.',
  method: 'Charged',
  prepaid: 'Prepaid package',
  recurring: 'Every period',
  occasional: 'When it adds up',
  everyMonths: (n: number) => (n === 1 ? 'every month' : `every ${n} months`),
  rate: 'Rate',
  perHour: '/h',
  noRate: 'No rate set',
  amount: 'Amount',
  open: 'Open',
  closed: 'Closed',
  createDraft: 'Draft an invoice',
  markInvoiced: 'Invoiced',
  markSent: 'Sent',
  reopen: 'Reopen',
  invoiceNumber: 'Invoice number',
  onceItHasOne: 'once it has one',
  discard: 'Discard',
  confirmDiscard: 'Throw this document away and put its hours back?',
  nothingToInvoice: 'Nothing waiting to be invoiced.',
  overrun: 'past the package',
  period: 'Period',
  upTo: 'Up to',
  statement: 'Statement of hours',
  invoiceDraft: 'Invoice draft',
  invoicedElsewhere: 'Invoiced through the old process',
  createStatement: 'Draft a statement',
  paidUpFront: 'Paid up front. This accounts for the hours; it does not charge for them.',
  print: 'Print',
  openDocument: 'Open',
  noValue: 'not charged',

  name: 'Name',
  jobTitle: 'Role',
  language: 'Language',
  timezone: 'Timezone',
  notifications: 'E-mail',
  notifyAll: 'Every time something happens',
  notifyDigest: 'Once a morning',
  notifyNone: 'Never',
  save: 'Save',
  saved: 'Saved',
  theme: 'Theme',
  dark: 'Dark',
  light: 'Light',
  all: 'All',
  loading: 'Loading',
  notFound: 'Not found.',
  back: 'Back',
};

type Dictionary = typeof en;

const pt: Dictionary = {
  signInTitle: 'O teu workspace',
  signInLead: 'Põe o teu e-mail e enviamos-te uma ligação. Não há palavra-passe.',
  email: 'E-mail',
  send: 'Enviar',
  linkSent: 'Vê o teu e-mail. A ligação funciona uma vez e dura vinte minutos.',
  signingIn: 'A entrar',
  signIn: 'Entrar',
  signOut: 'Sair',
  continueAs: (name: string) => `Continuar como ${name}`,
  linkTrouble: 'Pedir outra ligação',

  projects: 'Projetos',
  tasks: 'Tarefas',
  time: 'Tempo',
  people: 'Pessoas',
  workspace: 'Cliente',
  profile: 'Perfil',
  settings: 'Definições',
  editProfile: 'Editar os teus dados',
  backToOverview: 'Voltar à tua página',
  files: 'Ficheiros',
  about: 'Sobre',
  history: 'Histórico',

  noProjects: 'Ainda não há nada aqui.',
  nothingInStatus: (status: string) => `Nada em ${status.toLowerCase()}.`,
  showAll: 'Mostrar todos os projetos',
  noTasks: 'Sem tarefas.',
  noTime: 'Sem tempo registado.',
  noComments: 'Ainda sem comentários.',

  hoursUsed: 'usadas',
  hoursOf: 'de',
  hoursOver: 'acima de',
  openTasks: (n: number) => (n === 1 ? '1 tarefa aberta' : `${n} tarefas abertas`),
  taskCount: (n: number) => (n === 1 ? '1 tarefa' : `${n} tarefas`),
  projectCount: (n: number) => (n === 1 ? '1 projeto' : `${n} projetos`),
  newTask: 'Nova tarefa',
  createTask: 'Criar tarefa',
  title: 'Título',
  description: 'Descrição',
  priority: 'Prioridade',
  assignee: 'Atribuída a',
  state: 'Estado',
  approve: 'Aprovar',
  approveHint: 'Isto está à tua espera.',
  comment: 'Comentário',
  comments: 'Comentários',
  writeComment: 'Escreve um comentário',
  internalComment: 'Nota interna: só a equipa a vê',
  internalBadge: 'Interna',
  post: 'Publicar',
  imageHint: 'Escreve aqui, e cola ou arrasta imagens diretamente.',
  uploadFailed: 'Uma imagem não carregou. Tenta outra vez.',
  imageSize: 'Tamanho da imagem',
  formatting: 'Formatação',
  bold: 'Negrito',
  italic: 'Itálico',
  bulletList: 'Lista',
  numberedList: 'Lista numerada',
  link: 'Ligação',
  linkAddress: 'Endereço da ligação (deixa vazio para a retirar)',
  image: 'Imagem',
  logTime: 'Registar tempo',
  date: 'Data',
  started: 'Começou',
  minutes: 'Minutos',
  what: 'O quê',
  project: 'Projeto',
  person: 'Pessoa',
  hours: 'Horas',
  download: 'Descarregar CSV',
  recentTime: 'As tuas duas semanas',
  myTasks: 'As tuas tarefas',
  myTasksHint: 'Atribuídas a ti, novas ou em curso.',
  total: 'Total',
  seeEntries: 'Ver os registos',
  entries: (n: number) => (n === 1 ? '1 registo' : `${n} registos`),
  from: 'De',
  to: 'Até',
  clear: 'Limpar',
  remove: 'Apagar',
  confirmRemove: 'Apagar este registo?',
  confirmDeleteComment: 'Apagar este comentário? Não há forma de o recuperar.',
  addPerson: 'Adicionar alguém',
  noPeople: 'Ainda ninguém.',
  confirmRemovePerson: 'Tirar esta pessoa do projeto? Deixa de o ver.',
  onADocument: 'Num documento aberto',
  onADocumentHint: 'Estas horas estão num documento que ainda ninguém fechou. Para as mudar, deita-o fora.',
  noFilters: 'Tudo, do mais recente para trás.',
  shortBy: (h: string) => `faltam ${h}h`,
  nothingLogged: 'Sem registo',
  export: 'Exportar',
  client: 'Cliente',
  users: 'Pessoas',
  newUser: 'Adicionar alguém',
  invite: 'Adicionar',
  role: 'Função',
  team: 'Equipa Omibee',
  administrator: 'Administrador',
  active: 'Ativo',
  inactive: 'Desativado',
  showInactive: 'Mostrar desativados',
  workspaces: 'Clientes',
  edit: 'Editar',
  cancel: 'Cancelar',
  newProject: 'Novo projeto',
  editProject: 'Editar projeto',
  status: 'Estado',
  totalHours: 'Horas contratadas',
  startDate: 'Início',
  endDate: 'Fim',
  manager: 'Gestor',
  lastSeen: 'Último acesso',
  chooseEntries: 'Escolher entradas',
  hideEntries: 'Esconder entradas',
  leftOut: (n: number) => (n === 1 ? '1 entrada fica para a próxima' : `${n} entradas ficam para a próxima`),
  never: 'Nunca',
  owner: 'Responsável',
  published: 'Visível ao cliente',
  create: 'Criar',
  none: '—',
  search: 'Procurar',
  menu: 'Menu',
  close: 'Fechar',
  clients: 'Clientes',
  newClient: 'Adicionar cliente',
  clientName: 'Nome',
  lastWorked: 'Último trabalho',
  hoursByMonth: 'Horas, por mês',
  members: 'Quem vê isto',
  noActivity: 'Ainda sem registos.',
  bought: 'contratadas',
  hoursLeft: 'por usar',
  editClient: 'Editar cliente',
  billing: 'Faturação',
  toInvoice: 'À espera de fatura',
  documents: 'Documentos',
  documentKind: 'Tipo',
  noDocuments: 'Ainda não há documentos.',
  method: 'Cobrança',
  prepaid: 'Pacote pré-pago',
  recurring: 'Por período',
  occasional: 'Quando houver',
  everyMonths: (n: number) => (n === 1 ? 'todos os meses' : `de ${n} em ${n} meses`),
  rate: 'Tarifa',
  perHour: '/h',
  noRate: 'Sem tarifa',
  amount: 'Valor',
  open: 'Aberto',
  closed: 'Fechado',
  createDraft: 'Criar rascunho',
  markInvoiced: 'Faturado',
  markSent: 'Enviado',
  reopen: 'Reabrir',
  invoiceNumber: 'Número da fatura',
  onceItHasOne: 'quando tiver',
  discard: 'Descartar',
  confirmDiscard: 'Descartar o rascunho e devolver as horas?',
  nothingToInvoice: 'Nada à espera de fatura.',
  overrun: 'acima do pacote',
  period: 'Período',
  upTo: 'Até',
  statement: 'Relatório de horas',
  invoiceDraft: 'Rascunho de fatura',
  invoicedElsewhere: 'Faturado pelo processo antigo',
  createStatement: 'Criar relatório',
  paidUpFront: 'Já pago. Isto presta contas das horas, não as cobra.',
  print: 'Imprimir',
  openDocument: 'Abrir',
  noValue: 'não cobrado',

  name: 'Nome',
  jobTitle: 'Função',
  language: 'Idioma',
  timezone: 'Fuso horário',
  notifications: 'E-mail',
  notifyAll: 'Sempre que acontece alguma coisa',
  notifyDigest: 'Uma vez por manhã',
  notifyNone: 'Nunca',
  save: 'Guardar',
  saved: 'Guardado',
  theme: 'Tema',
  dark: 'Escuro',
  light: 'Claro',
  all: 'Todos',
  loading: 'A carregar',
  notFound: 'Não encontrado.',
  back: 'Voltar',
};

export const dictionaries: Record<Locale, Dictionary> = { en, pt };

/** State and status names, in the words the reader would use. "In stage" is a
 *  studio word; what the client is being told is that it waits for them. */
export const stateNames: Record<Locale, Record<string, string>> = {
  en: {
    new: 'New', in_progress: 'In progress', waiting_client: 'Waiting for you',
    in_stage: 'Waiting for your approval', closed: 'Closed',
  },
  pt: {
    new: 'Nova', in_progress: 'Em curso', waiting_client: 'À tua espera',
    in_stage: 'À espera da tua aprovação', closed: 'Fechada',
  },
};

export const staffStateNames: Record<Locale, Record<string, string>> = {
  en: {
    new: 'New', in_progress: 'In progress', waiting_client: 'Waiting for client',
    in_stage: 'In stage', closed: 'Closed',
  },
  pt: {
    new: 'Nova', in_progress: 'Em curso', waiting_client: 'À espera do cliente',
    in_stage: 'Em stage', closed: 'Fechada',
  },
};

export const statusNames: Record<Locale, Record<string, string>> = {
  en: {
    planning: 'Planning', in_progress: 'In progress', on_hold: 'On hold',
    complete: 'Complete', archived: 'Archived', placeholder: 'Not started',
  },
  pt: {
    planning: 'Planeamento', in_progress: 'Em curso', on_hold: 'Em pausa',
    complete: 'Concluído', archived: 'Arquivado', placeholder: 'Por começar',
  },
};

export const priorityNames: Record<Locale, Record<string, string>> = {
  en: { low: 'Low', medium: 'Medium', high: 'High' },
  pt: { low: 'Baixa', medium: 'Média', high: 'Alta' },
};
